import {
  BackgroundContainer,
  Container,
  Excerpt,
  Heading,
  TextContainer,
  BulletPointsContainer,
  ButtonsContainer,
} from "./styles";
import BulletPointComponent from "./BulletPoint";
import { useEffect, useRef } from "react";
import gsap, { SplitText } from "@/lib/gsap";
import { useTheme } from "styled-components";
import ExternalLinkComponent from "./ExternalLink";
import InternalLinkComponent, {
  InternalLinkProps,
} from "./InternalLink/InternalLink";
import CallToActionComponent from "@/components/CallToAction";
import _SplitText from "gsap/SplitText";
import { debounce } from "lodash";

// Cast to fix React 17 + @types/react 18 compatibility issue
const BulletPoint = BulletPointComponent as any;
const ExternalLink = ExternalLinkComponent as any;
const InternalLink = InternalLinkComponent as any;
const CallToAction = CallToActionComponent as any;

type InternalLinkButton = InternalLinkProps;

type ExternalLinkButton = {
  _type: "externalLink";
  title: string;
  url: string;
  blank: boolean;
};

type DownloadLinkButton = {
  _type: "downloadLink";
  title: string;
  file: {
    asset: {
      url: string;
    };
  };
};

type ProductSearchButton = {
  _type: "object";
  title: string;
  params: string;
  page: {
    _type: string;
    slug: string;
  };
};

type CallToActionButton = {
  _type: "callToAction";
  icon: string;
  variant: string;
} & (
  | { action: "internalLink"; internalLink: Omit<InternalLinkButton, "_type"> }
  | { action: "externalLink"; externalLink: Omit<ExternalLinkButton, "_type"> }
  | { action: "downloadLink"; downloadLink: Omit<DownloadLinkButton, "_type"> }
  | {
      action: "productSearch";
      productSearch: Omit<ProductSearchButton, "_type">;
    }
);

type Button = { _key: string } & (
  | InternalLinkButton
  | ExternalLinkButton
  | CallToActionButton
);

type Props = {
  heading: string | null;
  excerpt: string | null;
  bulletPoints: any[] | null;
  buttons: Button[] | null;
};

const TextWithBulletPoints: React.FC<Props> = (props) => {
  const { heading, excerpt, bulletPoints, buttons } = props;
  const theme = useTheme();

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);
  const bulletPointsContainerRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const excerptRef = useRef<HTMLDivElement>(null);

  const headingTextRef = useRef<_SplitText | null>(null);
  const excerptTextRef = useRef<_SplitText | null>(null);

  useEffect(() => {
    // Create closure around mutable ref
    const container = containerRef.current;
    const heading = headingRef.current;
    const excerpt = excerptRef.current;
    const buttonsContainer = buttonsContainerRef.current;
    const bulletPointsContainer = bulletPointsContainerRef.current;
    if (!bulletPointsContainer || !buttonsContainer) return;

    // Setup elements for animation
    const bulletPoints = Array.from(bulletPointsContainer.children);
    headingTextRef.current = new SplitText(heading, {
      type: "lines",
      linesClass: "lines",
    });
    excerptTextRef.current = new SplitText(excerpt, {
      type: "lines",
      linesClass: "lines",
    });
    const headingText = headingTextRef.current;
    const excerptText = excerptTextRef.current;
    const revertText = debounce(() => {
      if (headingText && excerptText) {
        headingText?.revert();
        excerptText?.revert();
      }
    }, 30);

    window.addEventListener("resize", revertText);
    gsap.set([heading, excerpt], { opacity: 1.0 });

    const tl = gsap
      .timeline({
        defaults: {
          duration: 0.3,
        },
        scrollTrigger: {
          trigger: container,
          toggleActions: "play pause resume reverse",
          start: "top 75%",
        },
      })
      .to(container, {
        opacity: 1.0,
        y: 0,
      })
      .to(
        [
          headingText.lines,
          excerptText.lines,
          buttonsContainer.children,
          bulletPoints,
        ],
        {
          opacity: 1.0,
          y: 0,
          stagger: { amount: 0.3 },
        },
        "<25%"
      );

    tl.call(() => {
      bulletPoints.forEach((element) => {
        gsap.to(element, {
          color: theme.colors.red,
          scrollTrigger: {
            trigger: element,
            scrub: 1,
            start: "top center",
            end: "center bottom",
          },
        });
      });
    });

    return function cleanup() {
      window.removeEventListener("resize", revertText);
      tl.kill();
      gsap.killTweensOf([bulletPoints]);
    };
  }, [theme.colors.red]);

  return (
    <BackgroundContainer ref={containerRef}>
      <Container>
        <TextContainer>
          <Heading ref={headingRef}>{heading}</Heading>
          <Excerpt ref={excerptRef}>{excerpt}</Excerpt>
          <ButtonsContainer ref={buttonsContainerRef}>
            {buttons?.map((button, index) => {
              if (!button?._key) return null;
              switch (button?._type) {
                case "externalLink":
                  return (
                    <ExternalLink key={button._key} {...button} index={index} />
                  );
                case "internalLink":
                  return (
                    <InternalLink key={button._key} {...button} index={index} />
                  );
                case "callToAction":
                  const commonProps = {
                    as: "a" as "a",
                    key: button._key,
                    icon: button.icon as any,
                    variant: button.variant as any,
                  };
                  if (button.action === "internalLink") {
                    return (
                      <CallToAction
                        {...commonProps}
                        action="internalLink"
                        internalLink={button.internalLink}
                      />
                    );
                  } else if (button.action === "externalLink") {
                    return (
                      <CallToAction
                        {...commonProps}
                        action="externalLink"
                        externalLink={button.externalLink}
                      />
                    );
                  } else if (button.action === "downloadLink") {
                    return (
                      <CallToAction
                        {...commonProps}
                        action="downloadLink"
                        downloadLink={button.downloadLink}
                      />
                    );
                  } else if (button.action === "productSearch") {
                    return (
                      <CallToAction
                        {...commonProps}
                        action="productSearch"
                        productSearch={button.productSearch}
                      />
                    );
                  }
                default:
                  return null;
              }
            })}
          </ButtonsContainer>
        </TextContainer>
        <BulletPointsContainer ref={bulletPointsContainerRef}>
          {bulletPoints?.map((item) => {
            if (!item?._key) return null;
            return <BulletPoint key={item._key} {...item} />;
          })}
        </BulletPointsContainer>
      </Container>
    </BackgroundContainer>
  );
};

export default TextWithBulletPoints;
